import type React from 'react'
import { Table } from '@/components/ui/table'
import { cn } from '@/lib/cn'
import { fmtMoney, formatLongDate } from './format'
import type { BalanceSheetResponse } from '@/features/reporting/types'

export interface BalanceSheetDrillArgs {
  mainCategory: string
  subcategory: string
  endDate: string
  startDate?: string
}

interface Props {
  data: BalanceSheetResponse
  tableRef?: React.MutableRefObject<HTMLTableElement | null>
  /**
   * Optional - when provided, clicking a leaf cell opens a drill-down.
   * The Smart Chat surface omits this to keep the chat view read-only;
   * the Reporting page passes a handler that opens the DrillDownModal.
   */
  onDrillDown?: (args: BalanceSheetDrillArgs) => void
}

export function BalanceSheetReport({ data, tableRef, onDrillDown }: Props) {
  const periods = data.periods
  const multiCol = periods.length > 1
  const totalCols = periods.length + 1 + (multiCol ? 1 : 0)

  const sections: Array<{ name: string; totalKey: 'totalAssets' | 'totalLiabilities' | 'totalEquity' }> = [
    { name: 'Assets', totalKey: 'totalAssets' },
    { name: 'Liabilities', totalKey: 'totalLiabilities' },
    { name: 'Equity', totalKey: 'totalEquity' },
  ]

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table className="w-full min-w-[640px] text-sm" ref={tableRef}>
        <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Account</th>
            {periods.map((p) => (
              <th key={p.asOfDate} className="px-3 py-2 text-right">
                As of {formatLongDate(p.asOfDate)}
              </th>
            ))}
            {multiCol ? <th className="px-3 py-2 text-right">Variance</th> : null}
          </tr>
        </thead>
        <tbody>
          {sections.map((section) => {
            const allCats = new Set<string>()
            periods.forEach((p) => {
              if (p.sections[section.name]) Object.keys(p.sections[section.name]).forEach((c) => allCats.add(c))
            })
            const sortedCats = [...allCats].sort()

            return (
              <>
                <tr key={`s-${section.name}`} className="bg-muted/70 font-bold">
                  <td colSpan={totalCols} className="px-3 py-1.5">
                    {section.name}
                  </td>
                </tr>
                {sortedCats.map((cat) => {
                  const allSubs = new Set<string>()
                  periods.forEach((p) => {
                    if (p.sections[section.name]?.[cat])
                      Object.keys(p.sections[section.name][cat]).forEach((x) => allSubs.add(x))
                  })
                  const sortedSubs = [...allSubs].sort()
                  const catTotals = periods.map(() => 0)

                  return (
                    <>
                      <tr key={`c-${section.name}-${cat}`} className="bg-muted/40 font-semibold">
                        <td colSpan={totalCols} className="px-3 py-1 pl-6">
                          {cat}
                        </td>
                      </tr>
                      {sortedSubs.map((sub) => (
                        <tr key={`l-${section.name}-${cat}-${sub}`} className="border-t">
                          <td className="px-3 py-1.5 pl-8">{sub}</td>
                          {periods.map((p, idx) => {
                            const val = p.sections[section.name]?.[cat]?.[sub] ?? 0
                            catTotals[idx] += val
                            const clickable = !!onDrillDown
                            return (
                              <td
                                key={`${p.asOfDate}-${idx}`}
                                className={cn(
                                  'px-3 py-1.5 text-right',
                                  clickable && 'cursor-pointer text-primary hover:underline',
                                )}
                                onClick={
                                  clickable
                                    ? () =>
                                        onDrillDown({
                                          mainCategory: cat,
                                          subcategory: sub,
                                          endDate: p.asOfDate,
                                        })
                                    : undefined
                                }
                              >
                                {fmtMoney(val)}
                              </td>
                            )
                          })}
                          {multiCol ? (
                            <td className="px-3 py-1.5 text-right">
                              {fmtMoney(
                                (periods[1].sections[section.name]?.[cat]?.[sub] ?? 0) -
                                  (periods[0].sections[section.name]?.[cat]?.[sub] ?? 0),
                              )}
                            </td>
                          ) : null}
                        </tr>
                      ))}
                      <tr key={`st-${section.name}-${cat}`} className="border-t bg-muted/30 font-semibold">
                        <td className="px-3 py-1.5">Total {cat}</td>
                        {catTotals.map((t, i) => (
                          <td key={i} className="px-3 py-1.5 text-right">
                            {fmtMoney(t)}
                          </td>
                        ))}
                        {multiCol ? (
                          <td className="px-3 py-1.5 text-right">{fmtMoney(catTotals[1] - catTotals[0])}</td>
                        ) : null}
                      </tr>
                    </>
                  )
                })}
                <tr key={`g-${section.name}`} className="border-t-2 bg-muted font-bold">
                  <td className="px-3 py-1.5">TOTAL {section.name.toUpperCase()}</td>
                  {periods.map((p, i) => (
                    <td key={i} className="px-3 py-1.5 text-right">
                      {fmtMoney(p.totals[section.totalKey])}
                    </td>
                  ))}
                  {multiCol ? (
                    <td className="px-3 py-1.5 text-right">
                      {fmtMoney(periods[1].totals[section.totalKey] - periods[0].totals[section.totalKey])}
                    </td>
                  ) : null}
                </tr>
                <tr key={`sep-${section.name}`}>
                  <td colSpan={totalCols} className="py-1" />
                </tr>
              </>
            )
          })}
          {periods.map((p) => (
            <tr key={`val-${p.asOfDate}`}>
              <td colSpan={totalCols} className="px-3 py-1.5 text-sm">
                <span className={cn(p.isBalanced ? 'text-success' : 'text-destructive', 'font-semibold')}>
                  {p.isBalanced ? 'Balanced' : 'OUT OF BALANCE'}
                </span>{' '}
                as of {formatLongDate(p.asOfDate)} - Assets: {fmtMoney(p.totals.totalAssets)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}
